/**
 * Workspace layout tree — binary tree of splits and panels.
 *
 * SplitNode has exactly two children (each panel or another split).
 * PanelNode is a leaf containing one or more tabbed view instances.
 */
export interface ViewInstance {
  instanceId: string
  viewId: string
  params?: Record<string, string>
  /**
   * Per-instance working state, owned by the rendered view via `useViewState`.
   *
   * Lives on the instance (not in the page's component state) because
   * PanelContainer mounts only the panel's ACTIVE tab — switching tabs unmounts
   * the view and would otherwise destroy everything it was holding. Keyed by
   * whatever string the view passes; opaque to the workspace layer.
   *
   * Persisted with the rest of the tree, so it also survives a page reload.
   * Keep entries small and JSON-serializable — this rides in localStorage.
   */
  viewState?: Record<string, unknown>
}

export interface PanelNode {
  type: 'panel'
  id: string
  views: ViewInstance[]
  activeViewInstanceId: string
}

export interface SplitNode {
  type: 'split'
  id: string
  direction: 'horizontal' | 'vertical'
  sizes: number[]
  children: [WorkspaceNode, WorkspaceNode]
}

export type WorkspaceNode = PanelNode | SplitNode

export const isPanelNode = (n: WorkspaceNode): n is PanelNode => n.type === 'panel'
export const isSplitNode = (n: WorkspaceNode): n is SplitNode => n.type === 'split'

// ─── Factories ──────────────────────────────────────────────────────

let _counter = 0
export function genId(prefix: string): string {
  _counter += 1
  return `${prefix}-${Date.now().toString(36)}-${_counter}`
}

export function createViewInstance(viewId: string, params?: Record<string, string>): ViewInstance {
  return { instanceId: genId('vi'), viewId, params }
}

export function createPanelNode(views: ViewInstance[], activeIdx = 0): PanelNode {
  return {
    type: 'panel',
    id: genId('panel'),
    views,
    activeViewInstanceId: views[Math.min(activeIdx, views.length - 1)].instanceId,
  }
}

export function createSplitNode(
  direction: 'horizontal' | 'vertical',
  children: [WorkspaceNode, WorkspaceNode],
  sizes: number[] = [50, 50],
): SplitNode {
  return { type: 'split', id: genId('split'), direction, children, sizes }
}
